import React, { useEffect, useState } from 'react';
import { initiateWalletTopupPayPal, refreshWalletTopupStatus } from '../utils/payments';

type TopupStatus = { status: 'created' | 'processing' | 'pending' | 'completed' | 'failed' | 'cancelled'; amountUsd: number; balanceUsd?: number | null; intentId?: string };

const TOPUP_MESSAGES: Record<TopupStatus['status'], string> = {
  created: 'Choose PayPal or Debit/Credit Card below to complete your payment.',
  processing: 'Confirming your payment with PayPal…',
  pending: 'PayPal is still processing this payment. Your wallet will be credited automatically once it completes.',
  completed: 'Payment confirmed. Your wallet has been credited.',
  failed: 'Payment failed. You have not been charged.',
  cancelled: 'Payment cancelled. You have not been charged.',
};

const WalletTopupPayPal: React.FC<{ amount: number; onCredited: () => Promise<void> | void; onClose: () => void }> = ({ amount, onCredited, onClose }) => {
  const [state, setState] = useState<TopupStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intentRef = React.useRef<string | null>(null);
  const onCreditedRef = React.useRef(onCredited);
  onCreditedRef.current = onCredited;

  useEffect(() => {
    let active = true;
    initiateWalletTopupPayPal('wallet-paypal-button', {
      amountUSD: amount,
      onStatus: (s) => {
        if (!active) return;
        const next = s as TopupStatus;
        if (next.intentId) intentRef.current = next.intentId;
        setError(null);
        setState(next);
        if (next.status === 'completed') void onCreditedRef.current();
      },
      onError: (msg) => { if (active) setError(msg); },
    })
      .then((intentId) => { intentRef.current = intentId; })
      .catch((e: any) => { if (active) setError(e?.message || 'Failed to start PayPal checkout'); });
    return () => { active = false; };
  }, [amount]);

  // Poll while PayPal reports the capture as pending (credited via verified webhook).
  useEffect(() => {
    if (state?.status !== 'pending' || !intentRef.current) return;
    const iv = setInterval(async () => {
      try {
        const p = await refreshWalletTopupStatus(intentRef.current as string);
        if (p.status !== 'pending') {
          setState(p as TopupStatus);
          if (p.status === 'completed') void onCreditedRef.current();
        }
      } catch { /* keep polling */ }
    }, 10000);
    return () => clearInterval(iv);
  }, [state?.status]);

  const done = state && ['completed', 'failed', 'cancelled'].includes(state.status);
  const tone = state?.status === 'completed' ? '#155724' : state?.status === 'failed' || error ? '#721c24' : 'var(--color-text-primary)';
  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-surface)', maxWidth: 420 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong>Fund wallet: ${amount.toFixed(2)} USD</strong>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)' }}>Close</button>
      </div>
      {(state || error) && (
        <div role="status" style={{ fontSize: 13, color: tone, marginBottom: 8 }}>
          {error || (state ? TOPUP_MESSAGES[state.status] : '')}
          {state?.status === 'completed' && typeof state.balanceUsd === 'number' && ` New balance: $${state.balanceUsd.toFixed(2)}.`}
        </div>
      )}
      <div id="wallet-paypal-button" style={{ display: done ? 'none' : 'block' }} />
    </div>
  );
};

export default WalletTopupPayPal;
